import React from "react";
import { Shield, Link2, Paperclip } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("de-CH"); } catch { return iso; }
};

export const COMPANION_LABEL = { return_slip: "Rückgabeschein", coupon: "Rabattcoupon", loyalty: "Kundenkarten-Bon", card_slip: "Kartenbeleg (Terminal)" };

/** Gebuchter Kaufbeleg (gleicher Bon / Lieferant+Datum) zu einem Garantieschein oder einer Kassenbon-Beilage. */
export const PurchaseReceiptLink = ({ item, compact, onAttachToggle }) => {
  const p = item.purchase_receipt;
  if (!p) {
    const pp = item.pending_purchase;
    if (!pp) return null;
    return (
      <div className={`${compact ? "" : "mt-2 "}flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900`} data-testid={`inbox-pending-purchase-${item.id}`}>
        <Link2 className="h-3.5 w-3.5 shrink-0 mt-px" />
        <span><strong>Kaufbeleg liegt noch im Posteingang</strong> · «{pp.file_name}»{pp.amount != null ? ` · CHF ${Number(pp.amount).toFixed(2)}` : ""}{pp.date ? ` · ${fmt(pp.date)}` : ""}. Zuerst den Kaufbeleg bestätigen – dann kann {item.kind === "warranty_certificate" ? "die Garantie" : "die Beilage"} damit verknüpft werden.</span>
      </div>
    );
  }
  const isCert = item.kind === "warranty_certificate";
  const certain = p.certain !== false;
  const attach = item._attach_cert !== false;
  return (
    <div className={`${compact ? "" : "mt-2 "}flex items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] ${certain ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid={`inbox-warranty-purchase-${item.id}`}>
      <Link2 className="h-3.5 w-3.5 shrink-0 mt-px" />
      <span>
        <strong>{certain ? "Gehört zum gebuchten Kaufbeleg" : "Vermutlich zum gebuchten Kaufbeleg"}</strong>{p.invoice_number ? ` · Bon ${p.invoice_number}` : ""}{p.date ? ` · ${fmt(p.date)}` : ""}{p.amount != null ? ` · CHF ${Number(p.amount).toFixed(2)}` : ""}
        {p.confirmed_at ? ` · gebucht ${fmt(p.confirmed_at)}` : ""}{p.reason && !certain ? ` (${p.reason})` : ""}.
        {isCert ? " Die Garantie wird beim Ablegen mit diesem Beleg verknüpft – kein zweiter Aufwand." : " Wird als Seite zu diesem Beleg gelegt – kein zweiter Aufwand."}
        {isCert && onAttachToggle && (
          <label className="mt-1 flex items-center gap-1.5 cursor-pointer select-none font-medium" title="Garantieschein inkl. Garantiebedingungen zusätzlich als Beilage (Seite) am Kaufbeleg speichern">
            <input type="checkbox" className="h-3.5 w-3.5 accent-emerald-600" checked={attach} onChange={(e) => onAttachToggle(item, e.target.checked)} data-testid={`inbox-warranty-attach-toggle-${item.id}`} />
            <Paperclip className="h-3 w-3" /> Garantieschein als Beilage zum Kaufbeleg mitspeichern
          </label>
        )}
      </span>
    </div>
  );
};

/** Kassenbon-Beilage (Rückgabeschein, Coupon, Kundenkarten-Bon, Terminalbeleg) – kein eigener Aufwand. */
export const CompanionHint = ({ item, onAttachToggle }) => {
  if (item.kind !== "receipt_companion") return null;
  const c = item.companion || {};
  return (
    <>
      <div className="mt-2 inline-flex flex-wrap items-center gap-2 text-[11px] rounded border px-2 py-1 bg-stone-50 border-stone-300 text-stone-800" data-testid={`inbox-companion-hint-${item.id}`}>
        <span className="inline-flex items-center gap-1 font-medium"><Paperclip className="h-3 w-3" />{c.label || COMPANION_LABEL[c.companion_type] || "Kassenbon-Beilage"}</span>
        <span className="text-stone-600">Kein eigener Aufwand – gehört als Seite zum Kaufbeleg.</span>
      </div>
      <PurchaseReceiptLink item={item} onAttachToggle={onAttachToggle} />
      {!item.purchase_receipt && (
        <div className="mt-2 text-[11px] text-stone-600" data-testid={`inbox-companion-nomatch-${item.id}`}>Kein gebuchter Kaufbeleg gefunden (Bon-Nr./Trx-Ref/Lieferant+Datum) – Kaufbeleg zuerst bestätigen oder Typ auf «Beleg» ändern.</div>
      )}
    </>
  );
};

/** Garantiehinweis auf einem Kaufbeleg: Badge + Haken «auch als Garantie speichern». */
export const WarrantyHint = ({ item, onToggle, onAttachToggle }) => {
  const w = item.warranty;
  const isCert = item.kind === "warranty_certificate";
  if (!w?.detected && !isCert) return null;
  const parts = [w?.product_name, w?.warranty_months ? `${w.warranty_months} Monate` : null, w?.warranty_until ? `bis ${fmt(w.warranty_until)}` : null].filter(Boolean);
  return (
    <>
      <div
        className={`mt-2 inline-flex flex-wrap items-center gap-2 text-[11px] rounded border px-2 py-1 ${isCert ? "bg-violet-50 border-violet-200 text-violet-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}
        data-testid={`inbox-warranty-hint-${item.id}`}
      >
        <span className="inline-flex items-center gap-1 font-medium">
          <Shield className="h-3 w-3" />
          {isCert ? "Garantieschein" : "Garantie erkannt"}
          {parts.length > 0 && <span className="font-normal">· {parts.join(" · ")}</span>}
        </span>
        {isCert ? (
          <span className="text-violet-700">Kein Kaufbeleg – wird nicht gebucht, sondern im Garantiebereich abgelegt.</span>
        ) : (
          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" title="Beim Bestätigen wird der Beleg verbucht UND zusätzlich eine Garantie im Garantiebereich angelegt (mit Kaufbeleg verknüpft)">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-emerald-600"
              checked={w?.create !== false}
              onChange={(e) => onToggle(item, e.target.checked)}
              data-testid={`inbox-warranty-toggle-${item.id}`}
            />
            auch als Garantie speichern
          </label>
        )}
      </div>
      {isCert && <PurchaseReceiptLink item={item} onAttachToggle={onAttachToggle} />}
    </>
  );
};
